import React from "react";
import { useLocation } from "react-router-dom";

import PortalLayout from "./PortalLayout";
import type { BreadcrumbItem } from "./PortalLayout";
import { PORTAL_BRAND } from "../constants/brand";
import { buildAdminNav, resolveBreadcrumb } from "../constants/portalNav";
import { getUserFromCookie } from "../helpers/api/apiCore";
import { useComplianceCounts } from "../helpers/useComplianceCounts";
import { useOperationsCounts } from "../helpers/useQueueCounts";
import type { NavSection } from "./PortalLayout";
import AlertsBell from "../components/admin-alerts/AlertsBell";

// =============================================================================
// PortalRouteLayout — the authed layout for routes flagged `layout: "portal"`.
// -----------------------------------------------------------------------------
// Thin adapter between the Hyper route machinery and the decoupled PortalLayout
// shell: pulls the signed-in user, supplies the admin nav, and seeds the topbar
// breadcrumb. The breadcrumb is DERIVED from the nav structure as
// "admin / <section> / <page>" (see resolveBreadcrumb) rather than the route's
// own `breadcrumb` field. Detail pages append a dynamic trailing crumb at
// runtime via usePortalChrome() + withDetailCrumb().
//
// Selected per-route in routes/Routes.tsx; legacy routes keep using the old
// VerticalLayout, so un-migrated pages are untouched.
// =============================================================================

interface PortalRouteLayoutProps {
  route?: {
    name?: string;
    breadcrumb?: BreadcrumbItem[];
  };
  children?: React.ReactNode;
}

/**
 * Display name for the signed-in staff user. The login response
 * (UserResponseDto) is flat and splits the name across `name` + `surname`, so
 * neither field alone is a full name — joining them is what makes the header
 * read "Admin Admin" rather than "Admin".
 */
const displayName = (user: any): string =>
  [user?.name, user?.surname].filter(Boolean).join(" ").trim() ||
  user?.fullName ||
  "Admin";

// Sidebar badges: menu item key → the count of records waiting on an admin.
export const badgeFor = (counts: Record<string, number | undefined>): Record<string, number | undefined> => ({
  "waiting-approvals": counts.applications,
  "compliance-documents": counts.documents,
  "service-activation-requests": counts.activationRequests,
  // Supplier promotion cards waiting for an admin (the same count as the dashboard card).
  "admin-promotions": counts.promotedCards,
});

const withBadges = (nav: NavSection[], badges: Record<string, number | undefined>): NavSection[] =>
  nav.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      const n = badges[item.key];
      return n ? { ...item, badge: n > 99 ? "99+" : n } : item;
    }),
  }));

const PortalRouteLayout = ({ route, children }: PortalRouteLayoutProps) => {
  const counts = useComplianceCounts();
  // Shared (same query key) with the dashboard's Operations panel; the server
  // leaves out counts this admin has no permission for.
  const operations = useOperationsCounts();
  const user: any = getUserFromCookie() || {};
  const { pathname } = useLocation();

  const breadcrumb: BreadcrumbItem[] = resolveBreadcrumb(pathname, route?.name);
  const name = displayName(user);

  return (
    <PortalLayout
      company={{ label: PORTAL_BRAND, name }}
      user={{
        name,
        email: user?.email,
        avatarUrl: user?.avatar || user?.image,
      }}
      nav={withBadges(buildAdminNav(), badgeFor({ ...counts, ...operations } as Record<string, number | undefined>))}
      createCta={null}
      breadcrumb={breadcrumb}
      bell={<AlertsBell />}
    >
      {children}
    </PortalLayout>
  );
};

export default PortalRouteLayout;
